import React, { useMemo } from "react";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import { useDispatch, useSelector } from "react-redux";
import { KMenuDropDown, KIcon, KTable, KDialog, KButton } from "@kyburz/kdesign-system";

import { compareDatesForTable, formatDate } from "../../utils/timeUtils";
import { sortBooleanColumns } from "../../utils/utils";
import useSetState from "../../hooks/useTypedSetState";
import { VehicleTemplate } from "./facades/VehicleTemplate";
import { useGetVehicleTemplates } from "./requests/GetVehicleTemplates";
import { Customer2, RootStateInterface } from "../../interfaces/ReduxInterface";
import { setAlert } from "../../redux/actions/actions";
import { updateVehicleTemplates as updateVehicleTemplate } from "./requests/UpdateVehicleTemplate";
import { useDeleteVehicleTemplate } from "./requests/DeleteVehicleTemplate";

import AssignToCompanyDialog from "./components/AssignToCompany";

const useStyles = makeStyles(() => ({
  buttons: {
    display: "flex",
    justifyContent: "flex-end",
  },
  addingButton: {
    marginRight: 15,
  },
  tableContainer: {
    height: "calc(100% - 100px)",
  },
  scrollableCell: {
    overflowX: "auto",
    overflowY: "hidden",
    scrollbarWidth: "none",
    height: 20,
    "&::-webkit-scrollbar": {
      width: 0,
      height: 0,
    },
    "& > div": {
      width: "max-content",
    },
  },
}));

const initialSort = [{ id: "name", order: "asc" }];

const VehicleTemplates: React.FC = () => {
  const classes = useStyles();
  const { customers: companies } = useSelector(
    (state: RootStateInterface) => state.customers,
  );

  const history = useHistory();
  const dispatch = useDispatch();

  const [state, setState] = useSetState({
    selected: {} as Record<string, boolean>,
    someSelected: false,
    showAssignDialog: false,
    showDeleteDialog: false,
    deletedTemplate: null as VehicleTemplate | null,
    loadingUpdating: false,
  });

  const { data: templates = [], request: getTemplates } =
    useGetVehicleTemplates();

  const { request: deleteTemplate } = useDeleteVehicleTemplate();

  const columns = useMemo(
    () => [
      {
        Header: "Name",
        id: "name",
        accessor: (row: VehicleTemplate) => row.name,
        defaultCanSort: true,
        defaultCanFilter: true,
        width: 100,
        Cell: (row: any) => (
          <div className={classes.scrollableCell}>
            <div>{row.value}</div>
          </div>
        ),
      },
      {
        Header: "Description",
        id: "description",
        accessor: (row: VehicleTemplate) => row.description,
        Cell: (row: any) => (
          <div className={classes.scrollableCell}>
            <div>{row.value}</div>
          </div>
        ),
        defaultCanSort: false,
        defaultCanFilter: false,
        width: 120,
      },
      {
        Header: "Companies",
        id: "companies",
        accessor: (row: VehicleTemplate) => row.companies,
        Cell: (row: any) => (
          <div className={classes.scrollableCell}>
            <div>{row.value?.join(", ")}</div>
          </div>
        ),
        defaultCanSort: false,
        defaultCanFilter: true,
        width: 90,
      },
      {
        Header: "Is Default",
        id: "isDefault",
        accessor: (row: VehicleTemplate) => row.isDefaultTemplate,
        Cell: (row: any) =>
          row?.value ? (
            <KIcon
              icon="check"
              color="#27AE60"
              style={{ flex: 1, display: "flex", justifyContent: "center" }}
            />
          ) : (
            ""
          ),
        defaultCanSort: true,
        defaultCanFilter: false,
        sortType: (rowA, rowB) => {
          const defaultA: boolean = rowA.original.is_default_template;
          const defaultB: boolean = rowB.original.is_default_template;
          return sortBooleanColumns(defaultA, defaultB);
        },
        width: 65,
      },
      {
        id: "created",
        Header: "Created",
        accessor: (row: VehicleTemplate) => formatDate(row.created),
        sortType: (rowA, rowB) => {
          const dateA = rowA.original.created;
          const dateB = rowB.original.created;
          return compareDatesForTable(dateA, dateB);
        },
        defaultCanSort: true,
        defaultCanFilter: false,
      },
      {
        id: "modified",
        Header: "Modified",
        accessor: (row: VehicleTemplate) => formatDate(row.modified),
        sortType: (rowA, rowB) => {
          const dateA = rowA.original.created;
          const dateB = rowB.original.created;
          return compareDatesForTable(dateA, dateB);
        },
        defaultCanSort: true,
        defaultCanFilter: false,
      },
      {
        accessor: "actions",
        defaultCanFilter: false,
        sticky: "right",
        Cell: (row) => (
          <KMenuDropDown
            menuItems={[
              {
                icon: "edit",
                title: "Edit",
                action: () =>
                  history.push(
                    `/config/vehicle/templates/templates/${row?.row?.original?.id}/editor`,
                  ),
              },
              {
                icon: "trash-2",
                title: "Delete",
                action: () =>
                  setState({
                    showDeleteDialog: true,
                    deletedTemplate: row?.row?.original || null,
                  }),
              },
            ]}
          />
        ),
        width: 50,
      },
    ],
    [history, setState],
  );

  const onApplyAssign = (selectedCompanies: Customer2[]) => {
    setState({ loadingUpdating: true });

    const requests = Object.keys(state.selected).map((index) => {
      const template = templates?.[parseInt(index, 10)];
      if (template) {
        const newCompanies = [
          ...template.companies,
          ...selectedCompanies.map(({ shortname }) => shortname),
        ];

        return updateVehicleTemplate(
          new VehicleTemplate({ ...template.data, companies: newCompanies }),
        );
      }

      return Promise.reject();
    });

    Promise.all(requests)
      .then(() => setState({ showAssignDialog: false, loadingUpdating: false }))
      .catch((response) => {
        setState({ showAssignDialog: false, loadingUpdating: false });
        dispatch(
          setAlert(`Template update failed! ${response?.details}`, "error"),
        );
      })
      .then(getTemplates);
  };

  const deleteDialog = (
    <KDialog
      size="xs"
      title="Delete Template"
      open={state.showDeleteDialog}
      primaryCTAonClick={() => {
        deleteTemplate({ templateId: state.deletedTemplate?.id })
          .then(() =>
            setAlert(
              `Template ${state.deletedTemplate?.name} deleted`,
              "success",
            ),
          )
          .catch((response) =>
            dispatch(
              setAlert(`Template delete failed! ${response?.details}`, "error"),
            ),
          )
          .then(() => setState({ showDeleteDialog: false }))
          .then(() => getTemplates());
      }}
      primaryCTAColor="danger"
      primaryCTAText="Delete"
      secondaryCTAonClick={() => setState({ showDeleteDialog: false })}
      secondaryCTAText="Cancel"
    >
      <p>Are you sure you want to delete {state.deletedTemplate?.name}?</p>
    </KDialog>
  );

  const assignToCompanyDialog = (
    <AssignToCompanyDialog
      loading={state.loadingUpdating}
      companies={companies}
      showDialog={state.showAssignDialog}
      onClose={() => setState({ showAssignDialog: false })}
      onApply={onApplyAssign}
    />
  );

  return (
    <>
      <div className={classes.buttons}>
        <KButton
          icon={<KIcon icon="plus" />}
          onClick={() => history.push("/config/vehicle/templates/templates/create/editor")}
          className={classes.addingButton}
        >
          New File
        </KButton>
        <KButton
          icon={<KIcon icon="briefcase" />}
          onClick={() => setState({ showAssignDialog: true })}
          disabled={!state.someSelected}
        >
          Assign to companies
        </KButton>
      </div>
      <div className={classes.tableContainer}>
        <KTable
          data={templates}
          columns={columns}
          hasFilter
          initialSortBy={initialSort}
          onSelectRows={(_, ids) =>
            setState({
              selected: ids,
              someSelected: Object.values(ids).some((id) => id),
            })
          }
        />
      </div>
      {assignToCompanyDialog}
      {deleteDialog}
    </>
  );
};

export default VehicleTemplates;
